import { Modes } from '@constants/canvas/layers';
import { CiaFilters } from '@context/ProjectFilters/types';
import {
  CiaCriteriaConfig,
  FilterSlot,
  ModelFilterCategory,
  ModelFiltersStore,
  ModelFilterState,
} from '@store/zustand/model-filters/types';
import { DEFAULT_SCOPE_TYPE, ScopeType } from '@utils/domains';
import { chipValueFits, summarizeSelection } from '@utils/filterSummary';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

/**
 * The three categories that live inside `ciaFilters` rather than as a flat
 * list. Their names double as the keys of `CiaFilters`, which is what lets
 * `setCategory` and `clearCategory` route by name.
 */
const CIA_CATEGORIES: ReadonlyArray<keyof CiaFilters> = [
  'assurance_level',
  'coverage',
  'applicability',
];

/**
 * Whether a category's selection lives inside `ciaFilters`, keyed by criteria,
 * rather than as a flat list on the state.
 */
export const isCiaCategory = (
  category: ModelFilterCategory,
): category is keyof CiaFilters =>
  CIA_CATEGORIES.includes(category as keyof CiaFilters);

const emptyState = (): ModelFilterState => ({
  domains: [],
  scopeType: DEFAULT_SCOPE_TYPE,
  validationStatus: [],
  reviewStatus: [],
  operationalStatuses: [],
  operatingModelStatuses: [],
  partners: [],
  cis: [],
  reportInclusion: [],
  ciaFilters: { assurance_level: {}, coverage: {}, applicability: {} },
});

const pickState = (store: ModelFiltersStore): ModelFilterState => ({
  domains: store.domains,
  scopeType: store.scopeType,
  validationStatus: store.validationStatus,
  reviewStatus: store.reviewStatus,
  operationalStatuses: store.operationalStatuses,
  operatingModelStatuses: store.operatingModelStatuses,
  partners: store.partners,
  cis: store.cis,
  reportInclusion: store.reportInclusion,
  ciaFilters: store.ciaFilters,
});

const hasCiaAnswers = (field: Record<string, string[]> | undefined) =>
  Object.values(field ?? {}).some((answers) => answers?.length);

/**
 * One section of a CIA selection reduced to the criteria and answers
 * `configured` offers. The section comes back by reference when nothing in
 * it had to go, so the caller can tell a change from a no-op without a
 * deep comparison.
 */
const pruneSection = (
  section: Record<string, string[]>,
  configured: CiaCriteriaConfig[keyof CiaFilters]['criterias'],
): Record<string, string[]> => {
  const next: Record<string, string[]> = {};
  let changed = false;

  Object.entries(section).forEach(([criteriaId, answers]) => {
    const offered = configured.find(({ id }) => id === criteriaId)?.answers;

    if (!offered) {
      changed = true;

      return;
    }

    const kept = answers.filter((answer) => offered.includes(answer));

    if (kept.length !== answers.length) {
      changed = true;
    }

    next[criteriaId] = kept.length === answers.length ? answers : kept;
  });

  return changed ? next : section;
};

/**
 * The CIA selection with every criteria the configuration no longer has
 * dropped and every answer a criteria no longer offers removed — what a
 * Project-settings save or a project load calls for, since a stale
 * criteria id matches no entity and a stale answer matches none either. A
 * criteria whose answers all went stays as an empty list, the same shape
 * a panel's Clear all leaves. The selection and each of its sections keep
 * their identity when nothing in them changed, so a store write can be
 * skipped by reference.
 */
export const pruneCiaSelection = (
  selection: CiaFilters,
  criteria: CiaCriteriaConfig,
): CiaFilters => {
  const pruned: CiaFilters = {
    assurance_level: pruneSection(
      selection.assurance_level,
      criteria.assurance_level.criterias,
    ),
    coverage: pruneSection(selection.coverage, criteria.coverage.criterias),
    applicability: pruneSection(
      selection.applicability,
      criteria.applicability.criterias,
    ),
  };

  const isSame = CIA_CATEGORIES.every(
    (category) => pruned[category] === selection[category],
  );

  return isSame ? selection : pruned;
};

/**
 * The slot a mode's selection lives in under a Share filters setting: Cyber
 * resilience always its own, Business alignment and Controls their own while
 * sharing is off and the one `shared` slot while it is on. No mode, no slot.
 */
export const resolveFilterSlot = (
  mode: Modes | null,
  shareFilters: boolean,
): FilterSlot | null => {
  if (mode === null) {
    return null;
  }

  if (mode === Modes.RiskManagement) {
    return Modes.RiskManagement;
  }

  return shareFilters ? 'shared' : mode;
};

/**
 * The bucket key a workspace, mode and Share filters setting resolve to:
 * `{workspace}/{slot}`, or the bare workspace while no mode is known.
 */
export const resolveScopeKey = (
  workspaceKey: string | null,
  mode: Modes | null,
  shareFilters: boolean,
): string | null => {
  if (!workspaceKey) {
    return null;
  }

  const slot = resolveFilterSlot(mode, shareFilters);

  return slot ? `${workspaceKey}/${slot}` : workspaceKey;
};

const slotOf = (key: string): string => key.slice(key.lastIndexOf('/') + 1);

/**
 * The buckets left once every one parked under the given slots, in any
 * workspace, is dropped. A workspace id never ends in a slot name — it is a
 * project or scenario id, or `{id}/scenario` — so the last path segment of a
 * key is its slot.
 */
const withoutSlots = (
  buckets: Record<string, ModelFilterState>,
  slots: ReadonlyArray<FilterSlot>,
): Record<string, ModelFilterState> =>
  Object.fromEntries(
    Object.entries(buckets).filter(
      ([key]) => !(slots as ReadonlyArray<string>).includes(slotOf(key)),
    ),
  );

/**
 * The selection `key` restores from `buckets`, and the buckets left once it
 * has. A slot that can hold a selection — any but Cyber resilience — with no
 * bucket of its own adopts the one pending under its bare workspace key, where
 * a selection made before the mode was known is parked; the pending bucket is
 * consumed, so it is adopted once. Everything else restores its own bucket or
 * starts empty.
 */
const restoreBucket = (
  buckets: Record<string, ModelFilterState>,
  key: string | null,
  workspaceKey: string | null,
  mode: Modes | null,
): {
  restored: ModelFilterState;
  buckets: Record<string, ModelFilterState>;
} => {
  if (!key) {
    return { restored: emptyState(), buckets };
  }

  if (buckets[key]) {
    return { restored: buckets[key], buckets };
  }

  const canAdopt =
    workspaceKey !== null && mode !== null && mode !== Modes.RiskManagement;
  const pending = canAdopt ? buckets[workspaceKey] : undefined;

  if (!pending) {
    return { restored: emptyState(), buckets };
  }

  return {
    restored: pending,
    buckets: Object.fromEntries(
      Object.entries(buckets).filter(([name]) => name !== workspaceKey),
    ),
  };
};

/**
 * The selection that writing `values` into `category` produces, without
 * writing it. `setCategory` applies exactly this, and the popover evaluates
 * the shared predicate against it before the write, so an open drawer can be
 * closed before the canvas hides its entity. A CIA category needs the
 * `criteriaId` the values belong to; without one there is nowhere to put
 * them, and the state comes back as it was, by identity.
 */
export const withCategorySelection = (
  state: ModelFilterState,
  category: ModelFilterCategory,
  values: string[],
  criteriaId?: string,
): ModelFilterState => {
  if (!isCiaCategory(category)) {
    return { ...state, [category]: values };
  }

  if (!criteriaId) {
    return state;
  }

  return {
    ...state,
    ciaFilters: {
      ...state.ciaFilters,
      [category]: { ...state.ciaFilters[category], [criteriaId]: values },
    },
  };
};

/**
 * The selection a Scope type switch produces, without writing it: the new
 * type with the Scope ids that survive it, every other category as it was.
 * `setScopeType` applies exactly this, and the popover judges the open
 * drawer against it before the write.
 */
export const withScopeType = (
  state: ModelFilterState,
  scopeType: ScopeType,
  domains: string[],
): ModelFilterState => ({ ...state, scopeType, domains });

/**
 * How many categories currently carry a selection — the AC4.3.1 badge value.
 *
 * Counts categories, not values: two Scope values give "1". The three CIA
 * rows each count separately because AC3.1 lists them as three rows, so a
 * selection under `coverage` alone reads "1" rather than folding into one CIA
 * category. A CIA criteria whose answer list is empty does not count.
 */
export const activeCategoryCount = (state: ModelFilterState): number => {
  const flat = [
    state.domains,
    state.validationStatus,
    state.reviewStatus,
    state.operationalStatuses,
    state.operatingModelStatuses,
    state.partners,
    state.cis,
    state.reportInclusion,
  ].filter((values) => values.length > 0).length;

  const cia = CIA_CATEGORIES.filter((category) =>
    hasCiaAnswers(state.ciaFilters[category]),
  ).length;

  return flat + cia;
};

/**
 * AC3.2's category-row summary: "Any", the single name, both names, or
 * "N selected". A value with no label falls back to the raw value rather than
 * disappearing from the summary.
 *
 * Whether two names are shown joined is measured against the applied-filter
 * chip's 240px value area beside `categoryLabel`, not estimated — the chip,
 * the root category row and the +N popover row all call this, so the three
 * texts cannot disagree. `fits` is injectable for tests.
 */
export const categorySummary = (
  values: string[],
  labels: Record<string, string>,
  categoryLabel: string,
  fits: (categoryLabel: string, text: string) => boolean = chipValueFits,
): string => {
  if (!values.length) {
    return 'Any';
  }

  const names = values.map((value) => labels[value] ?? value);

  return summarizeSelection(names, (text) => fits(categoryLabel, text));
};

/**
 * The values selected under one category, flattened across CIA criteria so
 * every category answers in the same shape regardless of how it is stored.
 */
export const categoryValues = (
  state: ModelFilterState,
  category: ModelFilterCategory,
): string[] => {
  if (isCiaCategory(category)) {
    return Object.values(state.ciaFilters[category] ?? {}).flat();
  }

  return state[category] ?? [];
};

/**
 * The model-filter selection, keyed by workspace and slot.
 *
 * The live selection sits at the top level so selectors stay flat. It
 * belongs to `buckets[scopeKey]`, where the key is the workspace — a project
 * or scenario id — joined to the slot its mode resolves to: Business
 * alignment and Controls each their own while `shareFilters` is off, the one
 * `shared` slot while it is on, and Cyber resilience always `risk-management`,
 * an empty slot nothing can edit. `setScope` parks the live selection under
 * its key and restores whichever bucket the new key owns, so switching mode
 * with sharing off brings back that mode's own selection, switching with it
 * on changes nothing, a scenario keeps its filters apart from its parent
 * project, and Cyber resilience leaves both modes' selections parked.
 *
 * `setShareFilters` re-keys without touching the live selection, so the
 * selection on screen is the one that carries over: on, it becomes the shared
 * one and every Business alignment and Controls bucket is dropped; off, it
 * stays with the current mode and every shared bucket is dropped, so the
 * other mode starts empty. Dropping across every workspace, not only the
 * current one, is what keeps a parked slot from resurfacing stale after the
 * flag has flipped twice: at any moment only the slots the current setting
 * can reach exist. A toggle leaves the selection as it was, which is not a
 * filter change, and `useFilters` emits nothing for it.
 *
 * While no mode is known the key is the bare workspace, and a selection made
 * then is pending: the first Business alignment, Controls or shared slot the
 * workspace resolves to with no bucket of its own adopts it, so the
 * `setScope` that brings the mode in keeps what is on screen rather than
 * parking it under a key nothing would read again. The questionnaire's "View
 * Controls" writes its answers before the mode is initialised on a fresh load
 * of that route, and this is what carries them into Controls. Cyber
 * resilience resolving first leaves the selection pending for the next mode.
 *
 * `clearAll` empties the live selection only — other buckets are untouched,
 * and so are the key and the flag; `reset` returns the whole store to its
 * initial state, flag and buckets included, so nothing survives a navigation
 * out of the project workspace. A selection made while `scopeKey` is `null`
 * belongs to no bucket, so the first `setScope` drops it rather than parking
 * it: set the key before the popover can take input.
 *
 * `reconcileCiaFilters` drops from the live selection whatever the
 * project's CIA configuration no longer offers — a criteria removed in
 * Project settings, an answer renamed there — and writes nothing when the
 * selection is already inside it. Parked buckets are left as they are; the
 * hook that drives this runs again when one of them becomes live.
 */
const useModelFiltersStore = create<ModelFiltersStore>((set, get) => ({
  ...emptyState(),
  scopeKey: null,
  workspaceKey: null,
  mode: null,
  shareFilters: false,
  buckets: {},

  setCategory: (category, values, criteriaId) => {
    const current = pickState(get());
    const next = withCategorySelection(current, category, values, criteriaId);

    if (next !== current) {
      set(next);
    }
  },

  setCiaFilters: (ciaFilters) => set({ ciaFilters }),

  setScopeType: (scopeType, domains) => set({ scopeType, domains }),

  reconcileCiaFilters: (criteria) => {
    const { ciaFilters } = get();
    const pruned = pruneCiaSelection(ciaFilters, criteria);

    if (pruned !== ciaFilters) {
      set({ ciaFilters: pruned });
    }
  },

  clearCategory: (category) => {
    if (isCiaCategory(category)) {
      const { ciaFilters } = get();

      set({ ciaFilters: { ...ciaFilters, [category]: {} } });

      return;
    }

    set({ [category]: [] } as Partial<ModelFiltersStore>);
  },

  clearAll: () => set(emptyState()),

  reset: () =>
    set({
      ...emptyState(),
      scopeKey: null,
      workspaceKey: null,
      mode: null,
      shareFilters: false,
      buckets: {},
    }),

  setScope: (workspaceKey, mode = null) => {
    const store = get();
    const key = resolveScopeKey(workspaceKey, mode, store.shareFilters);

    if (store.scopeKey === key) {
      if (store.workspaceKey !== workspaceKey || store.mode !== mode) {
        set({ workspaceKey, mode });
      }

      return;
    }

    const parked = store.scopeKey
      ? { ...store.buckets, [store.scopeKey]: pickState(store) }
      : store.buckets;

    const { restored, buckets } = restoreBucket(
      parked,
      key,
      workspaceKey,
      mode,
    );

    set({ ...restored, scopeKey: key, workspaceKey, mode, buckets });
  },

  setShareFilters: (shared) => {
    const store = get();

    if (store.shareFilters === shared) {
      return;
    }

    const dropped: FilterSlot[] = shared
      ? [Modes.BusinessAlignment, Modes.Controls]
      : ['shared'];

    set({
      shareFilters: shared,
      scopeKey: resolveScopeKey(store.workspaceKey, store.mode, shared),
      buckets: withoutSlots(store.buckets, dropped),
    });
  },
}));

export const useModelFilters = () =>
  useModelFiltersStore(useShallow(pickState));

export const useShareFilters = () =>
  useModelFiltersStore((state) => state.shareFilters);

export const useModelFilterActions = () =>
  useModelFiltersStore(
    useShallow(
      ({
        setCategory,
        setCiaFilters,
        setScopeType,
        reconcileCiaFilters,
        clearCategory,
        clearAll,
        reset,
        setScope,
        setShareFilters,
      }) => ({
        setCategory,
        setCiaFilters,
        setScopeType,
        reconcileCiaFilters,
        clearCategory,
        clearAll,
        reset,
        setScope,
        setShareFilters,
      }),
    ),
  );

export const getModelFiltersState = () => useModelFiltersStore.getState();

export default useModelFiltersStore;
